import { EventListItem, getDateTimeText, Pager, Search, VText } from '@vake/ui-kit';
import { Feature, FeatureCollection } from 'geojson';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { MatchingLabel } from '../../../shared/api-enums';
import { DisplayImage } from '../../../shared/components/Popup';
import { mapTags } from '../../../utils/map-tags';
import { useSetUpPager } from '../../../utils/setUpPager';
import { useOverwatchContext } from '../interactiveReport/overwatch-2-context';
import {
    darkLabel,
    darkVesselIcon,
    notAbleToVerifyLabel,
    notAbleToVerifyVesselIcon,
    verifiedLabel,
    verifiedVesselIcon,
} from '../interactiveReport/reportInfo/legend-data';
import { VesselInfo2 } from './overwatch-vessel-info-2';

type Props = {
    aisPositions: FeatureCollection;
    matchedVessels: FeatureCollection;
    darkVessels: FeatureCollection;
    windmills: FeatureCollection;
    platforms: FeatureCollection;
    selectedFeature: Feature | null;
    setSelectedFeature: (value: Feature | null) => void;
    setHoveredFeature: (value: Feature | null) => void;
    bookmarkedInsights: string[];
    setBookmarkedInsights: (uuid: string, insert: boolean) => void;
    viewedInsights: string[];
};
export const OverwatchRight2: FC<Props> = ({
    aisPositions,
    matchedVessels,
    darkVessels,
    windmills,
    platforms,
    selectedFeature,
    setSelectedFeature,
    setHoveredFeature,
    bookmarkedInsights,
    setBookmarkedInsights,
    viewedInsights,
}) => {
    const {
        filters: { query, setQuery },
    } = useOverwatchContext();
    const scrollerRef = useRef<HTMLDivElement>(null);
    const [scrollPosition, setScrollPosition] = useState<number>(0);
    const moveScroller = (position: number) => {
        const scroller = scrollerRef.current;
        if (scroller) {
            scroller.scrollTop = position;
        }
    };
    useEffect(() => {
        if (selectedFeature === null) {
            moveScroller(scrollPosition);
        }
    }, [selectedFeature, scrollPosition]);
    const saveScrollPosition = () => {
        const scroller = scrollerRef.current;
        if (scroller) {
            setScrollPosition(scroller.scrollTop);
        }
    };
    const allPositions = useMemo(() => {
        return [
            ...(darkVessels?.features || []),
            ...(matchedVessels?.features || []),
            ...(aisPositions?.features || []),
            ...(windmills?.features || []),
            ...(platforms?.features || []),
        ];
    }, [aisPositions, matchedVessels, darkVessels, windmills, platforms]);
    const {
        firstPage,
        lastPage,
        currentPage,
        setCurrentPage,
        startIndex,
        endIndex,
        numDataRows: numCaptures,
    } = useSetUpPager(allPositions ? allPositions.length : 0, 30);
    useEffect(() => {
        // If new data or changing page, the scroller moves to the top
        moveScroller(0);
    }, [currentPage, allPositions]);
    if (selectedFeature) {
        return (
            <VesselInfo2
                selectedFeature={selectedFeature}
                setSelectedFeature={setSelectedFeature}
                bookmarkedInsights={bookmarkedInsights}
                setBookmarkedInsights={setBookmarkedInsights}
            />
        );
    }
    return (
        <div className="h-full flex flex-col">
            <div className="p-4">
                <Search
                    placeholder={'Find by Name, MMSI, IMO'}
                    query={query}
                    handleChange={setQuery}
                    regexFilterOut={/[^ a-zA-Z0-9]/g}
                />
            </div>
            {numCaptures === 0 && (
                <div className="p-4 flex justify-center">
                    <VText variant="H4">No insights to view</VText>
                </div>
            )}
            <div className="flex flex-col flex-grow gap-1 items-center overflow-y-auto" ref={scrollerRef}>
                {allPositions.slice(startIndex, endIndex).map((item, index) => {
                    const matchingLabel = item.properties?.matching_label;
                    let icon = null;
                    let title = '';
                    if (matchingLabel === MatchingLabel.UNDETECTED) {
                        icon = notAbleToVerifyVesselIcon;
                        title = item.properties?.name
                            ? item.properties?.name
                            : item.properties?.mmsi
                            ? item.properties?.mmsi
                            : notAbleToVerifyLabel;
                    } else if (matchingLabel === MatchingLabel.DARK_VESSEL) {
                        icon = darkVesselIcon;
                        title = darkLabel;
                    } else if (matchingLabel === MatchingLabel.MATCHED) {
                        icon = verifiedVesselIcon;
                        title = item.properties?.name
                            ? item.properties?.name
                            : item.properties?.mmsi
                            ? item.properties?.mmsi
                            : verifiedLabel;
                    }
                    return (
                        <div
                            key={index}
                            onClick={() => {
                                saveScrollPosition();
                                setSelectedFeature(item);
                            }}
                            onMouseEnter={() => setHoveredFeature(item)}
                            onMouseLeave={() => setHoveredFeature(null)}
                        >
                            <EventListItem
                                title={title}
                                subtitle={
                                    item.properties?.timestamp
                                        ? getDateTimeText(new Date(item.properties?.timestamp))
                                        : ''
                                }
                                image={
                                    <DisplayImage
                                        imagePath={item.properties?.image_path}
                                        imageSource={item.properties?.image_source}
                                        lon={item.properties?.lon}
                                        lat={item.properties?.lat}
                                        timestamp={item.properties?.timestamp}
                                        mmsi={item.properties?.mmsi}
                                    />
                                }
                                icon={icon}
                                tags={item.properties?.tags ? mapTags(item.properties?.tags) : []}
                                bookmarked={bookmarkedInsights.includes(item.properties?.uuid)}
                                opened={viewedInsights.includes(item.properties?.uuid)}
                            />
                        </div>
                    );
                })}
            </div>
            {numCaptures && (
                <>
                    <div className="flex justify-center p-1">
                        <VText>
                            Images {startIndex + 1} to {endIndex} (out of {numCaptures})
                        </VText>
                    </div>
                    <div className="flex justify-center p-1">
                        <Pager first={firstPage} last={lastPage} current={currentPage} onChange={setCurrentPage} />
                    </div>
                </>
            )}
        </div>
    );
};
